import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useUIStore } from "@/workflow_api/store/uiStore";
import type { Input } from "@/workflow_api/types/workflow";
import type { GroupType } from "@/types/entities/approval-groups";
import { useGetGroups } from "@/queries/groups/groups.api";
import { getDeserializedGroupNode } from "../serialization";

const GroupsContext = createContext<{
  groups: GroupType[];
  selectedGroup: GroupType | null;
  loading: boolean;
  error: string | null;
  selectGroup: (group: GroupType | null) => void;
  loadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
}>({
  groups: [],
  selectedGroup: null,
  loading: false,
  error: null,
  selectGroup: () => {},
  loadMore: () => {},
  hasMore: false,
  isLoadingMore: false,
});

export const GroupsProvider = ({
  children,
  inputs,
}: {
  children: React.ReactNode;
  inputs: Input;
}) => {
  const { nodeDetailsOpen, selectedNodeId } = useUIStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [allGroups, setAllGroups] = useState<GroupType[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GroupType | null>(null);

  const {
    data: groupsResponse,
    isLoading,
    error,
    isFetching,
  } = useGetGroups({
    page: currentPage,
    per_page: 1000,
    order_by: "name",
  });

  const hasMore = groupsResponse
    ? currentPage < groupsResponse.meta.last_page
    : false;

  const isLoadingMore = isFetching && currentPage > 1;

  useEffect(() => {
    if (!nodeDetailsOpen) {
      setCurrentPage(1);
      setAllGroups([]);
      setSelectedGroup(null);
      return;
    }

    // reset state
    setCurrentPage(1);
    setAllGroups([]);
    setSelectedGroup(null);
  }, [nodeDetailsOpen, selectedNodeId]);

  // Update groups when new data comes in
  useEffect(() => {
    if (!groupsResponse?.data) return;

    if (currentPage === 1) {
      // First page - replace all groups
      setAllGroups(groupsResponse.data);
    } else {
      // Additional pages - append to existing groups
      setAllGroups((prev) => [...prev, ...groupsResponse.data]);
    }
  }, [groupsResponse?.data, currentPage]);

  // Deserialize selected group when first page is loaded
  useEffect(() => {
    if (
      currentPage === 1 &&
      allGroups.length > 0 &&
      !selectedGroup &&
      nodeDetailsOpen
    ) {
      const deserializedGroup = getDeserializedGroupNode(inputs, allGroups);
      if (deserializedGroup) {
        setSelectedGroup(deserializedGroup);
      }
    }
  }, [allGroups, inputs, selectedGroup, currentPage, nodeDetailsOpen]);

  // Load more function for infinite scroll
  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMore, isFetching]);

  return (
    <GroupsContext.Provider
      value={{
        groups: allGroups,
        selectedGroup,
        loading: isLoading,
        error: error?.message || null,
        selectGroup: setSelectedGroup,
        loadMore,
        hasMore,
        isLoadingMore,
      }}
    >
      {children}
    </GroupsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useGroups = () => {
  const context = useContext(GroupsContext);
  if (context === undefined) {
    throw new Error("useGroups must be used within a GroupsProvider");
  }
  return context;
};
